import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Form, Icon, Label, Loader, Message, Modal, Segment } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import { adminUserApi } from "../../api/api";
import DataTable from "./DataTable";

const EMPTY_ADMIN_USER = {
  username: "",
  password: "",
  email: "",
  first_name: "",
  last_name: "",
  designation: "",
  mobile: "",
  role: "GH_ADMIN",
  has_edit_access: false,
  guest_house_ids: [],
};

/* Roles that may hold the per-field indent editor flag. */
const ROLES_ALLOWING_EDIT_ACCESS = ["GH_ADMIN", "SUPER_ADMIN"];

const ROLE_OPTIONS = [
  { key: "GH_ADMIN", value: "GH_ADMIN", text: "Guest House Admin" },
  { key: "SUPER_ADMIN", value: "SUPER_ADMIN", text: "Super Admin" },
  /* Night desk: the check-in/check-out screen and nothing else. Still needs a
     guest house assigned, same as a GH admin — that assignment is what scopes
     the movements it sees. */
  { key: "NIGHT_STAFF", value: "NIGHT_STAFF", text: "Night Staff" },
];

/* Everyone but a super admin is scoped to the guest houses assigned to them, so
   an account in one of these roles is useless until it has at least one. */
const ROLES_NEEDING_GH = ["GH_ADMIN", "NIGHT_STAFF"];
const needsGuestHouse = (role) => ROLES_NEEDING_GH.includes(role);

/* Read the table's label off the same list the form offers, so a role added to
   ROLE_OPTIONS can't show up in the dropdown but be mislabelled in the table —
   which is what happened to NIGHT_STAFF when the column was a two-way ternary
   that called everything-but-SUPER_ADMIN a Guest House Admin. */
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((o) => [o.value, o.text]));
const ROLE_COLOUR = { SUPER_ADMIN: "green", NIGHT_STAFF: "blue", GH_ADMIN: "grey" };

const fieldError = (message) => (message ? { content: message, pointing: "below" } : false);

/* The API rejects a bad save with {field: ["message"]} (see `fieldErrors` in
   api.js). Pin each one against its own input rather than dropping the whole
   body into the banner, which is how the modal used to show raw JSON. Anything
   that isn't a form field — non_field_errors, or a key this form doesn't
   render — falls back to the banner so it can't disappear silently. */
const FORM_FIELDS = [
  "username", "password", "email", "first_name",
  "last_name", "designation", "mobile", "role", "has_edit_access",
];

const toFormErrors = (e, fallback) => {
  const out = {};
  const globals = [];
  for (const [key, value] of Object.entries(e.fieldErrors || {})) {
    const message = Array.isArray(value) ? value.join(" ") : String(value);
    if (FORM_FIELDS.includes(key)) out[key] = message;
    else globals.push(message);
  }
  if (globals.length || !Object.keys(out).length) {
    out._global = globals.join(" ") || e.message || fallback;
  }
  return out;
};

const columnHelper = createColumnHelper();

export default function AdminUsersTab({ guestHouses }) {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ADMIN_USER);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [assignGhSelect, setAssignGhSelect] = useState("");

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [quickUpdate, setQuickUpdate] = useState(null);
  const [quickForm, setQuickForm] = useState({ designation: "", mobile: "" });
  const [quickErrors, setQuickErrors] = useState({});
  const [quickSaving, setQuickSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminUserApi.list();
      setAdminUsers(data);
      return data;
    } catch (e) {
      setError(e.message || "Failed to load admin users.");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_ADMIN_USER);
    setErrors({});
    setAssignGhSelect("");
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.user?.username || "",
      password: "",
      email: u.user?.email || "",
      first_name: u.user?.first_name || "",
      last_name: u.user?.last_name || "",
      designation: u.designation || "",
      mobile: u.mobile || "",
      role: u.role,
      has_edit_access: u.has_edit_access || false,
      guest_house_ids: [],
    });
    setErrors({});
    setAssignGhSelect("");
    setModalOpen(true);
  };

  const handleModalAssignGh = async () => {
    if (!assignGhSelect || !editing) return;
    try {
      await adminUserApi.assignGh(editing.id, Number(assignGhSelect));
      const data = await load();
      const updated = data.find((u) => u.id === editing.id);
      if (updated) setEditing(updated);
      setAssignGhSelect("");
    } catch (e) {
      setErrors({ _global: e.message || "Failed to assign guest house." });
    }
  };

  const handleModalRemoveGh = async (ghId) => {
    if (!editing) return;
    try {
      await adminUserApi.removeGh(editing.id, ghId);
      const data = await load();
      const updated = data.find((u) => u.id === editing.id);
      if (updated) setEditing(updated);
    } catch (e) {
      setErrors({ _global: e.message || "Failed to remove guest house." });
    }
  };

  const updateForm = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!editing) {
      if (!form.username.trim()) e.username = "Required";
      if (!form.password.trim()) e.password = "Required";
    }
    if (!form.email.trim()) e.email = "Required";
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.mobile.trim()) e.mobile = "Required";
    return e;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (editing) {
        const updated = await adminUserApi.update(editing.id, {
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          designation: form.designation,
          mobile: form.mobile,
          has_edit_access: form.has_edit_access,
        });
        setAdminUsers((prev) =>
          prev.map((u) => (u.id === editing.id ? updated : u)),
        );
      } else {
        const created = await adminUserApi.create({
          username: form.username,
          password: form.password,
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          designation: form.designation,
          mobile: form.mobile,
          role: form.role,
          has_edit_access: form.has_edit_access,
        });
        for (const ghId of form.guest_house_ids) {
          await adminUserApi.assignGh(created.id, ghId);
        }
        await load();
      }
      setModalOpen(false);
    } catch (e) {
      setErrors(toFormErrors(e, "Failed to save admin user."));
    } finally {
      setSaving(false);
    }
  };

  const openQuickUpdate = (u) => {
    setQuickUpdate(u);
    setQuickForm({ designation: u.designation || "", mobile: u.mobile || "" });
    setQuickErrors({});
  };

  const handleQuickUpdate = async () => {
    const e = {};
    if (!quickForm.mobile.trim()) e.mobile = "Required";
    setQuickErrors(e);
    if (Object.keys(e).length) return;
    setQuickSaving(true);
    try {
      const updated = await adminUserApi.update(quickUpdate.id, {
        designation: quickForm.designation,
        mobile: quickForm.mobile,
      });
      setAdminUsers((prev) =>
        prev.map((u) => (u.id === quickUpdate.id ? updated : u)),
      );
      setQuickUpdate(null);
    } catch (e) {
      setQuickErrors(toFormErrors(e, "Failed to update admin user."));
    } finally {
      setQuickSaving(false);
    }
  };

  const handleRemoveGh = useCallback(
    async (userId, ghId) => {
      try {
        await adminUserApi.removeGh(userId, ghId);
        await load();
      } catch (e) {
        setError(e.message || "Failed to remove guest house.");
      }
    },
    [load],
  );

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminUserApi.delete(deleteConfirm.id);
      setAdminUsers((prev) => prev.filter((u) => u.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (e) {
      setError(e.message || "Failed to delete admin user.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (u) => [u.user?.first_name, u.user?.last_name].filter(Boolean).join(" "),
        { id: "name", header: "Name", cell: (info) => info.getValue() || "—" },
      ),
      columnHelper.accessor((u) => u.user?.username || "", {
        id: "username",
        header: "Username",
      }),
      columnHelper.accessor((u) => u.user?.email || "", {
        id: "email",
        header: "Email",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("designation", {
        header: "Designation",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("mobile", {
        header: "Mobile",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("role", {
        header: "Role",
        cell: (info) => (
          <Label color={ROLE_COLOUR[info.getValue()] || "grey"}>
            {ROLE_LABEL[info.getValue()] || info.getValue()}
          </Label>
        ),
      }),
      columnHelper.accessor("has_edit_access", {
        header: "Edit access",
        cell: (info) =>
          info.getValue() ? <Icon name="check" color="green" /> : "—",
      }),
      columnHelper.display({
        id: "assignedGh",
        header: "Assigned Guest House(s)",
        enableSorting: false,
        cell: (info) => {
          const u = info.row.original;
          return (u.assigned_guest_houses || []).length === 0 ? (
            "—"
          ) : (
            <Label.Group>
              {u.assigned_guest_houses.map((g) => (
                <Label key={g.guest_house_id} basic>
                  <Icon name="building outline" />
                  {g.guest_house__name}
                  <Icon
                    name="delete"
                    title="Remove guest house"
                    onClick={() => handleRemoveGh(u.id, g.guest_house_id)}
                  />
                </Label>
              ))}
            </Label.Group>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: (info) => {
          const u = info.row.original;
          return (
            <Button.Group size="small">
              <Button basic onClick={() => openEdit(u)}>
                <Icon name="edit" />
                Edit
              </Button>
              <Button basic onClick={() => openQuickUpdate(u)}>
                <Icon name="sync alternate" />
                Update
              </Button>
              <Button basic negative onClick={() => setDeleteConfirm(u)}>
                <Icon name="trash" />
                Delete
              </Button>
            </Button.Group>
          );
        },
      }),
    ],
    [handleRemoveGh],
  );

  return (
    <div>
      <Segment basic vertical textAlign="right">
        <Button primary onClick={openAdd}>
          <Icon name="user plus" />
          Create Admin User
        </Button>
      </Segment>

      {error && (
        <Message negative className="adm-notice">
          <Icon name="exclamation circle" />
          {error}
        </Message>
      )}

      {loading ? (
        <Segment basic>
          <Loader active inline="centered" size="large">
            Loading admin users…
          </Loader>
        </Segment>
      ) : (
        <DataTable
          data={adminUsers}
          columns={columns}
          emptyMessage="No admin users found."
        />
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name={editing ? "edit" : "user plus"} />
          {editing ? "Edit Admin User" : "Create Admin User"}
        </Modal.Header>
        <Modal.Content>
          <Form error={!!errors._global}>
            {errors._global && (
              <Message negative className="adm-notice" content={errors._global} />
            )}
            {!editing && (
              <Form.Group widths="equal">
                <Form.Input
                  label="Username"
                  required
                  value={form.username}
                  error={fieldError(errors.username)}
                  onChange={(e) => updateForm("username", e.target.value)}
                  autoComplete="off"
                  name="new-admin-username"
                />
                <Form.Input
                  label="Password"
                  required
                  type="password"
                  value={form.password}
                  error={fieldError(errors.password)}
                  onChange={(e) => updateForm("password", e.target.value)}
                  autoComplete="new-password"
                  name="new-admin-password"
                />
              </Form.Group>
            )}
            <Form.Group widths="equal">
              <Form.Input
                label="First Name"
                required
                value={form.first_name}
                error={fieldError(errors.first_name)}
                onChange={(e) => updateForm("first_name", e.target.value)}
              />
              <Form.Input
                label="Last Name"
                value={form.last_name}
                error={fieldError(errors.last_name)}
                onChange={(e) => updateForm("last_name", e.target.value)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Email"
                required
                value={form.email}
                error={fieldError(errors.email)}
                onChange={(e) => updateForm("email", e.target.value)}
              />
              <Form.Input
                label="Mobile"
                required
                value={form.mobile}
                error={fieldError(errors.mobile)}
                onChange={(e) => updateForm("mobile", e.target.value)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label="Designation"
                value={form.designation}
                error={fieldError(errors.designation)}
                onChange={(e) => updateForm("designation", e.target.value)}
              />
              {!editing && (
                <Form.Select
                  label="Role"
                  value={form.role}
                  options={ROLE_OPTIONS}
                  onChange={(_, data) => updateForm("role", data.value)}
                />
              )}
            </Form.Group>
            {ROLES_ALLOWING_EDIT_ACCESS.includes(editing ? editing.role : form.role) && (
              <Form.Checkbox
                label="Can edit any indent field"
                checked={form.has_edit_access}
                error={fieldError(errors.has_edit_access)}
                onChange={(_, d) => updateForm("has_edit_access", d.checked)}
              />
            )}
            {!editing && needsGuestHouse(form.role) && (
              <Form.Field>
                <label>Assign Guest House(s)</label>
                {form.guest_house_ids.length > 0 && (
                  <Label.Group style={{ marginBottom: 8 }}>
                    {form.guest_house_ids.map((id) => {
                      const gh = guestHouses.find((g) => g.id === id);
                      return (
                        <Label key={id} basic>
                          <Icon name="building outline" />
                          {gh?.name || id}
                          <Icon
                            name="delete"
                            title="Remove guest house"
                            onClick={() =>
                              updateForm(
                                "guest_house_ids",
                                form.guest_house_ids.filter((gid) => gid !== id),
                              )
                            }
                          />
                        </Label>
                      );
                    })}
                  </Label.Group>
                )}
                <Form.Group>
                  <Form.Select
                    width={12}
                    placeholder="+ Assign Guest House"
                    value={assignGhSelect}
                    options={guestHouses
                      .filter((gh) => !form.guest_house_ids.includes(gh.id))
                      .map((gh) => ({ key: gh.id, value: gh.id, text: gh.name }))}
                    onChange={(_, data) => setAssignGhSelect(data.value)}
                  />
                  <Form.Field width={4}>
                    <Button
                      type="button"
                      basic
                      fluid
                      disabled={!assignGhSelect}
                      onClick={() => {
                        const id = Number(assignGhSelect);
                        updateForm("guest_house_ids", [...form.guest_house_ids, id]);
                        setAssignGhSelect("");
                      }}
                    >
                      Assign
                    </Button>
                  </Form.Field>
                </Form.Group>
              </Form.Field>
            )}
            {editing && needsGuestHouse(editing.role) && (
              <Form.Field>
                <label>Guest House(s)</label>
                {(editing.assigned_guest_houses || []).length > 0 && (
                  <Label.Group style={{ marginBottom: 8 }}>
                    {editing.assigned_guest_houses.map((g) => (
                      <Label key={g.guest_house_id} basic>
                        <Icon name="building outline" />
                        {g.guest_house__name}
                        <Icon
                          name="delete"
                          title="Remove guest house"
                          onClick={() => handleModalRemoveGh(g.guest_house_id)}
                        />
                      </Label>
                    ))}
                  </Label.Group>
                )}
                <Form.Group>
                  <Form.Select
                    width={12}
                    placeholder="+ Assign Guest House"
                    value={assignGhSelect}
                    options={guestHouses
                      .filter(
                        (gh) =>
                          !(editing.assigned_guest_houses || []).some(
                            (a) => a.guest_house_id === gh.id,
                          ),
                      )
                      .map((gh) => ({ key: gh.id, value: gh.id, text: gh.name }))}
                    onChange={(_, data) => setAssignGhSelect(data.value)}
                  />
                  <Form.Field width={4}>
                    <Button
                      type="button"
                      basic
                      fluid
                      disabled={!assignGhSelect}
                      onClick={handleModalAssignGh}
                    >
                      Assign
                    </Button>
                  </Form.Field>
                </Form.Group>
              </Form.Field>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button primary loading={saving} disabled={saving} onClick={handleSave}>
            <Icon name="save" />
            {saving ? "Saving…" : editing ? "Save Changes" : "Create Admin User"}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        size="mini"
      >
        <Modal.Header>
          <Icon name="trash" /> Delete Admin User
        </Modal.Header>
        <Modal.Content>
          <p>
            Delete <strong>{deleteConfirm?.user?.username}</strong>?
          </p>
          <p className="bhu-confirm-sub">This cannot be undone.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button negative loading={deleting} disabled={deleting} onClick={handleDelete}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Quick Update Modal (PATCH designation/mobile) */}
      <Modal
        open={!!quickUpdate}
        onClose={() => setQuickUpdate(null)}
        size="mini"
        closeIcon
      >
        <Modal.Header>
          <Icon name="sync alternate" />
          Update Admin User
        </Modal.Header>
        <Modal.Content>
          <Form error={!!quickErrors._global}>
            {quickErrors._global && (
              <Message negative className="adm-notice" content={quickErrors._global} />
            )}
            <p className="bhu-confirm-sub" style={{ marginTop: 0 }}>
              Updating <strong>{quickUpdate?.user?.username}</strong>
            </p>
            <Form.Group widths="equal">
              <Form.Input
                label="Designation"
                value={quickForm.designation}
                error={fieldError(quickErrors.designation)}
                onChange={(e) =>
                  setQuickForm((f) => ({ ...f, designation: e.target.value }))
                }
              />
              <Form.Input
                label="Mobile"
                required
                value={quickForm.mobile}
                error={fieldError(quickErrors.mobile)}
                onChange={(e) =>
                  setQuickForm((f) => ({ ...f, mobile: e.target.value }))
                }
              />
            </Form.Group>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setQuickUpdate(null)}>
            Cancel
          </Button>
          <Button primary loading={quickSaving} disabled={quickSaving} onClick={handleQuickUpdate}>
            <Icon name="save" />
            {quickSaving ? "Updating…" : "Update"}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
